import React, { useState } from 'react';

import { Box, Button, Separator, Text, ToggleGroup } from '@betterment/samba-react';

import AllTimeContributions from './allTimeContributions';
import CurrentYearContributions from './currentYearContributions';

import { Employer } from '../../data';
import EmployerContributionDescriptionModal from '../employerContributionDescriptionModal';

interface Props {
  employer: Employer;
}

const EmployerContributions: React.FC<React.PropsWithChildren<Props>> = ({ employer }) => {
  const [timePeriodSelection, setTimePeriodSelection] = useState('currentYear');
  const [hasContributionDetailsModalOpen, setHasContributionDetailsModalOpen] = useState(false);

  if (!employer.contributionConfig && !employer.fourOhOneKContributions?.allTime?.amount?.cents) return null;

  const ctaDescription = employer.contributionConfig?.ctaDescription;

  return (
    <Box css={{ marginTop: 'space.200' }}>
      {hasContributionDetailsModalOpen ? (
        <EmployerContributionDescriptionModal
          employer={employer}
          closeHandler={() => setHasContributionDetailsModalOpen(false)}
        />
      ) : null}
      <Box
        css={{
          borderWidth: 'thin',
          borderStyle: 'solid',
          borderColor: 'border.weak',
          borderRadius: 'medium',
          backgroundColor: 'background.surface',
        }}
      >
        <Box css={{ padding: 'space.300' }}>
          <Text
            as="p"
            css={{
              marginBottom: 'space.0',
            }}
          >
            Employer match
          </Text>
          <Text
            as="p"
            color="text.weak"
            css={{
              fontSize: 'fontSize.50',
              marginBottom: 'space.200',
              _last: { marginBottom: 'space.0' },
            }}
          >
            {employer.name}
          </Text>
        </Box>
        <Separator />
        <Box css={{ padding: 'space.300' }}>
          {employer.contributionConfig && (
            <>
              {ctaDescription && (
                <Text
                  as="p"
                  color="text"
                  css={{
                    marginBottom: 'space.0',
                    fontWeight: 'normal',
                  }}
                >
                  {ctaDescription}
                </Text>
              )}
              <Button
                css={{ marginBottom: 'space.300', textStyle: 'text50' }}
                variant="textPrimary"
                onClick={() => setHasContributionDetailsModalOpen(true)}
              >
                See details
              </Button>
              <Separator css={{ marginBottom: 'space.200' }} />
            </>
          )}
          <ToggleGroup
            onValueChange={(value: string) => {
              if (value !== '') {
                setTimePeriodSelection(value);
              }
            }}
            spacing="default"
            type="single"
            value={timePeriodSelection}
          >
            <ToggleGroup.Item value="currentYear">
              <Text>This Year</Text>
            </ToggleGroup.Item>
            <ToggleGroup.Item value="allTime">
              <Text>All Time</Text>
            </ToggleGroup.Item>
          </ToggleGroup>
          {timePeriodSelection === 'currentYear' && employer.fourOhOneKContributions && (
            <CurrentYearContributions
              employerFourOhOneKContributionConfig={employer.contributionConfig!}
              employerFourOhOneKContributionsCurrentYear={employer.fourOhOneKContributions?.thisYear}
            />
          )}
          {timePeriodSelection === 'allTime' && employer.fourOhOneKContributions && (
            <AllTimeContributions employerFourOhOneKContributionsAllTime={employer.fourOhOneKContributions?.allTime} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EmployerContributions;
